import type { QuoteWatermark } from "@forgepoint/shared";
import {
  createContext,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  PdfPage,
  PAGE_CONTENT_HEIGHT,
  PAGE_HEIGHT_PX,
  PAGE_PADDING_X,
  PAGE_PADDING_Y,
  PAGE_WIDTH_PX,
} from "./PdfPage";
import { PageWatermark } from "./PageWatermark";
import {
  computeUnitRanges,
  findHeadingRunStart,
  isIndentContainerBlockquote,
  keepTogetherBoundaryFitsPage,
  readClosingEdgeHeight,
  readUnitBoundaries,
  REPEATED_HEADER_ATTRIBUTE,
  type UnitRange,
} from "./pageBreaks";

interface PdfPageInfo {
  pageNumber: number;
  totalPages: number;
}

const PdfPageContext = createContext<PdfPageInfo>({
  pageNumber: 0,
  totalPages: 0,
});

/**
 * Returns the current page number and total page count.
 * Only valid inside content rendered by PdfPageViewer.
 */
export function usePdfPageInfo(): PdfPageInfo {
  return useContext(PdfPageContext);
}

/**
 * A section's opt-in capability to be split into real per-page DOM.
 *
 * The section declares how many atomic units it holds and renders any range of
 * them as a standalone subtree; its measured render marks each unit's first
 * element with `data-fp-unit`. That is the whole contract - the paginator never
 * learns what a unit is, so a table splits into complete per-page tables (real
 * borders on both edges, no sliced strokes) without the paginator knowing that
 * tables exist. A section that declares no fragmenter is shown through the clip
 * window exactly as before.
 */
export interface SectionFragmenter {
  /** The number of atomic units the section holds, in document order. */
  unitCount: number;
  /**
   * Renders the units in `[startUnit, endUnit)` as a standalone subtree.
   * @param startUnit - The first unit index to render
   * @param endUnit - One past the last unit index to render
   * @returns The rendered fragment
   */
  renderUnits: (startUnit: number, endUnit: number) => ReactNode;
}

/** A discrete block of content that the paginator places onto pages. */
export interface QuoteSection {
  /** Unique key for React reconciliation. */
  key: string;
  /** The renderable content. */
  content: ReactNode;
  /** When true, this section always starts on a new page. */
  forcePageBreak?: boolean;
  /** When true, the repeatable header is not shown on this section's page. */
  skipHeader?: boolean;
  skipFirstHeader?: boolean;
  /** When true, the repeatable footer is not shown on this section's page. */
  skipFooter?: boolean;
  /** When true, draws a bottom border if this section lands last on a page,
   * sealing an open-bottomed table at the page break. */
  sealBottom?: boolean;
  /**
   * Chrome drawn in the top margin of the page this section starts, outside
   * the measured content flow, so it never shifts where page breaks land.
   * Used by the document editor for its selectable page-break affordance.
   */
  pageMarker?: ReactNode;
  /**
   * Opt-in capability letting an oversized section be split into real per-page
   * DOM instead of being shown through a clip window.
   */
  fragmenter?: SectionFragmenter;
}

interface PdfPageViewerProps {
  /** Ordered list of content sections to paginate. */
  sections: QuoteSection[];
  /** When true, keeps headings with content and splits oversized keep-together blocks. */
  useContentAwarePageBreaks?: boolean;
  /** Content rendered at the top of every page (e.g. company header). */
  repeatableHeader?: ReactNode;
  /** Content rendered at the bottom of every page (e.g. company footer). */
  repeatableFooter?: ReactNode;
  /**
   * Chrome drawn over every page (e.g. a static page frame), positioned
   * against the page box itself, outside the measured content flow.
   */
  pageOverlay?: ReactNode;
  watermark?: QuoteWatermark | null;
  /** Horizontal page padding in CSS pixels. */
  pagePaddingX?: number;
  /** Vertical page padding in CSS pixels. */
  pagePaddingY?: number;
}

/**
 * Measures section heights in a hidden container, distributes them across
 * US Letter-sized pages, and renders the paginated result.
 * Oversized sections are sliced at element boundaries across multiple pages.
 * @param sections - Content sections to paginate across pages
 * @param repeatableHeader - Optional content rendered at the top of every page
 * @param repeatableFooter - Optional content rendered at the bottom of every page
 */
export function PdfPageViewer({
  sections,
  useContentAwarePageBreaks = false,
  repeatableHeader,
  repeatableFooter,
  pageOverlay,
  watermark,
  pagePaddingX = PAGE_PADDING_X,
  pagePaddingY = PAGE_PADDING_Y,
}: PdfPageViewerProps) {
  const measurerRef = useRef<HTMLDivElement>(null);
  const headerMeasurerRef = useRef<HTMLDivElement>(null);
  const footerMeasurerRef = useRef<HTMLDivElement>(null);
  const [pages, setPages] = useState<PageLayout[]>([]);
  const pageContentWidth = PAGE_WIDTH_PX - pagePaddingX * 2;
  const pageContentHeight = PAGE_HEIGHT_PX - pagePaddingY * 2;

  useLayoutEffect(() => {
    if (!measurerRef.current || sections.length === 0) {
      setPages([]);
      return;
    }

    let isMounted = true;

    const measureAndPaginate = () => {
      if (!isMounted) {
        return;
      }

      const measurer = measurerRef.current;
      if (!measurer) {
        return;
      }

      const sectionElements = Array.from(measurer.children) as HTMLElement[];
      const heights = sectionElements.map((child) => child.offsetHeight);

      const headerHeight = headerMeasurerRef.current?.offsetHeight ?? 0;
      const footerHeight = footerMeasurerRef.current?.offsetHeight ?? 0;
      setPages(
        distributeToPages(
          sections,
          heights,
          sectionElements,
          headerHeight,
          footerHeight,
          useContentAwarePageBreaks,
          pageContentHeight,
        ),
      );
    };

    measureAndPaginate();

    const observedElements = [
      measurerRef.current,
      headerMeasurerRef.current,
      footerMeasurerRef.current,
    ].filter((element): element is HTMLDivElement => element !== null);

    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(measureAndPaginate);
    observedElements.forEach((element) => resizeObserver?.observe(element));

    document.fonts?.ready.then(measureAndPaginate).catch(() => undefined);

    return () => {
      isMounted = false;
      resizeObserver?.disconnect();
    };
  }, [
    sections,
    repeatableHeader,
    repeatableFooter,
    useContentAwarePageBreaks,
    pageContentHeight,
  ]);

  return (
    <>
      <style
        dangerouslySetInnerHTML={{
          __html: `
          @page {
            size: letter;
            margin: 0;
          }
          @media print {
            .pdf-page {
              box-sizing: border-box !important;
              width: 8.5in !important;
              height: 11in !important;
              padding: ${pagePaddingY}px ${pagePaddingX}px !important;
              box-shadow: none !important;
              break-before: page;
              page-break-before: always;
              break-inside: avoid;
              page-break-inside: avoid;
              overflow: hidden;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            .pdf-page:first-child {
              break-before: auto;
              page-break-before: auto;
            }
          }
        `,
        }}
      />

      {/* Off-screen measurers — same width as page content area */}
      <div
        aria-hidden
        ref={(el) => el?.setAttribute("inert", "")}
        className="fixed -left-[9999px] top-0 pointer-events-none"
        style={{ width: pageContentWidth }}
      >
        {/* overflow-hidden contains child margins, matching how the header
            and footer render on a page: as flex items, whose edges child
            margins cannot collapse through. A plain block div would let a
            trailing margin escape and under-measure the band. */}
        {repeatableHeader && (
          <div ref={headerMeasurerRef} className="overflow-hidden">
            {repeatableHeader}
          </div>
        )}
        {repeatableFooter && (
          <div ref={footerMeasurerRef} className="overflow-hidden">
            {repeatableFooter}
          </div>
        )}
        <div ref={measurerRef}>
          {sections.map((section) => (
            <div key={section.key} className="overflow-hidden">
              {section.content}
            </div>
          ))}
        </div>
      </div>

      {/* Visible pages */}
      <div className="py-8 space-y-8 flex flex-col items-center print:py-0 print:space-y-0">
        {pages.map((page, pageIndex) => (
          <PdfPage
            key={`page-${pageIndex}`}
            paddingX={pagePaddingX}
            paddingY={pagePaddingY}
          >
            <PdfPageContext.Provider
              value={{
                pageNumber: pageIndex + 1,
                totalPages: pages.length,
              }}
            >
              <div className="flex flex-col h-full">
                {page.showHeader && repeatableHeader}
                <div
                  className="overflow-hidden"
                  style={{ height: page.contentHeight }}
                >
                  {page.slicedContent ? (
                    <div
                      style={{
                        height: page.sliceHeight,
                        overflow: "hidden",
                        marginLeft: -40,
                        paddingLeft: 40,
                      }}
                    >
                      <div style={{ marginTop: -page.sliceOffset }}>
                        {page.slicedContent}
                      </div>
                    </div>
                  ) : (
                    page.sections.map((section, sectionIndex) => (
                      <div
                        key={section.key}
                        className={
                          section.sealBottom &&
                          sectionIndex === page.sections.length - 1
                            ? "overflow-hidden border-b border-gray-900"
                            : "overflow-hidden"
                        }
                      >
                        {section.content}
                      </div>
                    ))
                  )}
                </div>
                {page.showFooter && repeatableFooter}
              </div>
              {pageOverlay}
              {watermark && <PageWatermark watermark={watermark} />}
              {page.pageMarker && (
                <div className="absolute left-12 right-12 top-0 flex h-12 items-center print:hidden">
                  {page.pageMarker}
                </div>
              )}
              {!page.showHeader && !repeatableFooter && (
                <p className="absolute bottom-0 left-0 right-0 text-center text-[10px] text-gray-500 pb-4">
                  Page {pageIndex + 1} of {pages.length}
                </p>
              )}
            </PdfPageContext.Provider>
          </PdfPage>
        ))}
      </div>
    </>
  );
}

/** Layout metadata for a single rendered page. */
interface PageLayout {
  sections: QuoteSection[];
  showHeader: boolean;
  showFooter: boolean;
  /** Reserved height for the page body, matching pagination measurement. */
  contentHeight: number;
  /** For sliced oversized sections: the content to render. */
  slicedContent?: ReactNode;
  /** Pixels to offset the content upward to show the correct slice. */
  sliceOffset: number;
  /** Visible height for this slice. */
  sliceHeight: number;
  /** Chrome drawn in this page's top margin, outside the content flow. */
  pageMarker?: ReactNode;
}

/** A computed slice boundary for an oversized section. */
interface Slice {
  offset: number;
  height: number;
}

/**
 * Finds safe page break points within an oversized section by measuring
 * its block-level DOM children. Elements marked with data-pdf-keep-together
 * are treated as one block when they fit on a page.
 *
 * Child offsets come from getBoundingClientRect, which is scaled by any
 * ancestor CSS transform, while the page budget and the rendered slice offsets
 * are untransformed layout pixels. The measured scale therefore divides back
 * out, so a transformed ancestor - the editor's dialog runs a zoom-in
 * animation while this first measures - cannot land a break mid-element.
 * @param sectionEl - The section's DOM element from the measurer
 * @param pageHeight - Available content height per page, in layout pixels
 * @returns Array of slices, each with an offset and height in layout pixels
 */
function computeSlices(
  sectionEl: HTMLElement,
  pageHeight: number,
  useContentAwarePageBreaks: boolean,
  continuationHeight = pageHeight,
): Slice[] {
  const items = getSliceBoundaryElements(
    sectionEl,
    Math.min(pageHeight, continuationHeight),
    useContentAwarePageBreaks,
  );
  const totalHeight = sectionEl.offsetHeight;

  if (items.length === 0) {
    return [{ offset: 0, height: totalHeight }];
  }

  const sectionRect = sectionEl.getBoundingClientRect();
  const scale =
    totalHeight > 0 && sectionRect.height > 0
      ? sectionRect.height / totalHeight
      : 1;
  const slices: Slice[] = [];
  let pageStart = 0;

  items.forEach((item, itemIndex) => {
    const itemRect = item.getBoundingClientRect();
    const itemTop = (itemRect.top - sectionRect.top) / scale;
    const itemBottom = itemTop + itemRect.height / scale;

    const availableHeight =
      slices.length === 0 ? pageHeight : continuationHeight;
    if (itemBottom - pageStart > availableHeight && itemTop > pageStart) {
      const sliceEnd = useContentAwarePageBreaks
        ? findHeadingRunStart(
            items,
            itemIndex,
            item,
            sectionRect.top,
            scale,
            pageStart,
            availableHeight,
          )
        : itemTop;
      slices.push({
        offset: pageStart,
        height: sliceEnd - pageStart,
      });
      pageStart = sliceEnd;
    }
  });

  if (pageStart < totalHeight) {
    slices.push({
      offset: pageStart,
      height: totalHeight - pageStart,
    });
  }

  return slices;
}

/**
 * Collects the elements whose tops are valid page break points in a section.
 * @param sectionEl - The section's DOM element from the measurer
 * @param pageHeight - Available content height per page, in layout pixels
 * @param useContentAwarePageBreaks - Whether oversized keep-together blocks split
 * @returns The boundary elements, in document order
 */
function getSliceBoundaryElements(
  sectionEl: HTMLElement,
  pageHeight: number,
  useContentAwarePageBreaks: boolean,
): HTMLElement[] {
  const keepTogetherSelector = "[data-pdf-keep-together='true']";
  const blockSelectors = "li, p, h1, h2, h3, h4, h5, h6, blockquote, pre, tr";
  const elements = Array.from(
    sectionEl.querySelectorAll(`${keepTogetherSelector}, ${blockSelectors}`),
  ).filter((element): element is HTMLElement => element instanceof HTMLElement);

  return elements.filter((element) => {
    if (
      useContentAwarePageBreaks &&
      element.matches(keepTogetherSelector) &&
      !keepTogetherBoundaryFitsPage(element.offsetHeight, pageHeight)
    ) {
      return false;
    }

    if (
      !element.matches(keepTogetherSelector) &&
      isIndentContainerBlockquote(element, blockSelectors)
    ) {
      return false;
    }

    const keepTogetherAncestor =
      element.parentElement?.closest(keepTogetherSelector);

    if (!keepTogetherAncestor) {
      return true;
    }

    if (!(keepTogetherAncestor instanceof HTMLElement)) {
      return true;
    }

    return keepTogetherAncestor.offsetHeight > pageHeight;
  });
}

/**
 * The height a section reprints on every page after its first: the marked
 * column-header band of a table repeating its header. The measured flow holds
 * it once, so the paginator has to charge it back against the later pages.
 * @param sectionEl - The section's DOM element from the measurer
 * @returns The reprinted height in layout pixels, zero when nothing repeats
 */
function repeatedChromeHeight(sectionEl: HTMLElement): number {
  const repeated = sectionEl.querySelector(`[${REPEATED_HEADER_ATTRIBUTE}]`);
  return repeated instanceof HTMLElement ? repeated.offsetHeight : 0;
}

/**
 * Splits an oversized fragmentable section into one page per unit range, each
 * holding real DOM for its own units. Returns null when the section declares no
 * fragmenter or its measured units cannot be paginated (a single unit taller
 * than a page), leaving the caller on the clip-window path.
 * @param section - The oversized section
 * @param sectionEl - The section's DOM element from the measurer
 * @param pageHeight - Available content height per page, in layout pixels
 * @param showHeader - Whether the pages draw the repeatable header
 * @param showFooter - Whether the pages draw the repeatable footer
 * @returns One page per unit range, or null when the section must not fragment
 */
function fragmentToPages(
  section: QuoteSection,
  sectionEl: HTMLElement,
  pageHeight: number,
  showHeader: boolean,
  showFooter: boolean,
  continuationHeight = pageHeight,
  continuationShowHeader = showHeader,
): PageLayout[] | null {
  const fragmenter = section.fragmenter;
  if (!fragmenter) {
    return null;
  }
  const ranges = computeUnitRanges(
    readUnitBoundaries(sectionEl),
    sectionEl.offsetHeight,
    fragmenter.unitCount,
    pageHeight,
    repeatedChromeHeight(sectionEl),
    readClosingEdgeHeight(sectionEl),
    continuationHeight,
  );
  if (ranges === null) {
    return null;
  }
  return ranges.map((range: UnitRange, rangeIndex: number) => ({
    sections: [
      {
        ...section,
        key: `${section.key}-fragment-${rangeIndex}`,
        sealBottom: false,
        content: fragmenter.renderUnits(range.start, range.end),
      },
    ],
    showHeader: rangeIndex === 0 ? showHeader : continuationShowHeader,
    showFooter,
    contentHeight: rangeIndex === 0 ? pageHeight : continuationHeight,
    sliceOffset: 0,
    sliceHeight: 0,
    pageMarker: rangeIndex === 0 ? section.pageMarker : undefined,
  }));
}

/**
 * Distributes sections across pages using a greedy bin-packing algorithm.
 * Sections with `forcePageBreak` always start on a new page.
 * Sections with `skipHeader` suppress the repeatable header on their page.
 * Sections with `skipFooter` suppress the repeatable footer on their page.
 * Sections taller than a single page are sliced at element boundaries.
 * @param sections - Sections to distribute
 * @param heights - Measured pixel heights of each section
 * @param sectionElements - DOM elements from the measurer for child measurement
 * @param repeatableHeaderHeight - Height reserved for the repeatable header on every page
 * @param repeatableFooterHeight - Height reserved for the repeatable footer on every page
 * @param useContentAwarePageBreaks - Whether to enable opt-in content-aware page breaks
 * @returns Array of page layouts with sections and display metadata
 */
export function distributeToPages(
  sections: QuoteSection[],
  heights: number[],
  sectionElements: HTMLElement[],
  repeatableHeaderHeight: number,
  repeatableFooterHeight: number,
  useContentAwarePageBreaks: boolean,
  pageContentHeight = PAGE_CONTENT_HEIGHT,
): PageLayout[] {
  const SAFETY_MARGIN = 8;

  const getAvailableHeight = (skipsHeader: boolean, skipsFooter: boolean) =>
    pageContentHeight -
    SAFETY_MARGIN -
    (skipsHeader ? 0 : repeatableHeaderHeight) -
    (skipsFooter ? 0 : repeatableFooterHeight);

  const result: PageLayout[] = [];
  let currentSections: QuoteSection[] = [];
  let currentHeight = 0;
  let currentPageSkipsHeader = false;
  let currentPageSkipsFooter = false;

  const flushPage = () => {
    if (currentSections.length === 0) {
      return;
    }
    result.push({
      sections: currentSections,
      showHeader: !currentPageSkipsHeader,
      showFooter: !currentPageSkipsFooter,
      contentHeight: getAvailableHeight(
        currentPageSkipsHeader,
        currentPageSkipsFooter,
      ),
      sliceOffset: 0,
      sliceHeight: 0,
      pageMarker: currentSections[0].pageMarker,
    });
    currentSections = [];
    currentHeight = 0;
  };

  sections.forEach((section, index) => {
    const sectionHeight = heights[index] ?? 0;

    if (section.forcePageBreak && currentSections.length > 0) {
      flushPage();
    }

    if (currentSections.length === 0) {
      currentPageSkipsHeader =
        (section.skipHeader ?? false) || (section.skipFirstHeader ?? false);
      currentPageSkipsFooter = section.skipFooter ?? false;
    }

    const pageHeight = getAvailableHeight(
      currentPageSkipsHeader,
      currentPageSkipsFooter,
    );

    if (
      currentHeight + sectionHeight > pageHeight &&
      currentSections.length > 0
    ) {
      flushPage();
      currentPageSkipsHeader =
        (section.skipHeader ?? false) || (section.skipFirstHeader ?? false);
      currentPageSkipsFooter = section.skipFooter ?? false;
    }

    const availableHeight = getAvailableHeight(
      currentPageSkipsHeader,
      currentPageSkipsFooter,
    );

    if (sectionHeight > availableHeight && currentSections.length === 0) {
      const continuationSkipsHeader = section.skipHeader ?? false;
      const continuationHeight = getAvailableHeight(
        continuationSkipsHeader,
        currentPageSkipsFooter,
      );
      const fragmentPages = fragmentToPages(
        section,
        sectionElements[index],
        availableHeight,
        !currentPageSkipsHeader,
        !currentPageSkipsFooter,
        continuationHeight,
        !continuationSkipsHeader,
      );
      if (fragmentPages !== null) {
        result.push(...fragmentPages);
        return;
      }

      const slices = computeSlices(
        sectionElements[index],
        availableHeight,
        useContentAwarePageBreaks,
        continuationHeight,
      );
      slices.forEach((slice, sliceIndex) => {
        result.push({
          sections: [],
          showHeader:
            sliceIndex === 0
              ? !currentPageSkipsHeader
              : !continuationSkipsHeader,
          showFooter: !currentPageSkipsFooter,
          contentHeight:
            sliceIndex === 0 ? availableHeight : continuationHeight,
          slicedContent: section.content,
          sliceOffset: slice.offset,
          sliceHeight: slice.height,
          pageMarker: sliceIndex === 0 ? section.pageMarker : undefined,
        });
      });
      return;
    }

    currentSections.push(section);
    currentHeight += sectionHeight;
  });

  flushPage();

  return result;
}
